interface GoogleWindow extends Window {
  google?: typeof google;
  initMap?: () => void; // Or whatever your initMap function signature is
}

let googleMapsScriptLoaded = false;
let loadingPromise: Promise<void> | null = null;

/**
 * Ensures the Google Maps API script is loaded.
 * @param apiKey The Google Maps API key.
 * @returns A promise that resolves when the script is loaded.
 */
export const ensureGoogleMapsScriptLoaded = (apiKey: string): Promise<void> => {
  const globalWindow = window as GoogleWindow;

  if (globalWindow.google && globalWindow.google.maps) {
    googleMapsScriptLoaded = true;
  }

  if (googleMapsScriptLoaded) {
    return Promise.resolve();
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = new Promise((resolve, reject) => {
    // Check if script is already in the document (e.g. added by another instance/hook)
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript && globalWindow.google && globalWindow.google.maps) {
        googleMapsScriptLoaded = true;
        resolve();
        return;
    }
    
    if (existingScript && !globalWindow.google) {
        // Script tag exists but google object not yet available, wait for it
        const oldOnLoad = existingScript.onload;
        existingScript.onload = () => {
            if (typeof oldOnLoad === 'function') {
                oldOnLoad.call(existingScript);
            }
            googleMapsScriptLoaded = true;
            resolve();
        };
        existingScript.onerror = (event: Event | string) => {
            console.error('Google Maps API script loading error (existing script):', event);
            reject(new Error('Failed to load Google Maps API from existing script.'));
        };
        return;
    }


    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';

    script.onload = () => {
      googleMapsScriptLoaded = true;
      resolve();
    };

    script.onerror = (event: Event | string) => {
      console.error('Google Maps API script loading error:', event);
      loadingPromise = null; // Reset promise so it can be tried again
      reject(new Error('Failed to load Google Maps API.'));
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
};

/**
 * Defines the possible outcomes of a geocoding attempt.
 */
export type GeocodeResult =
  | { success: true; coordinates: { latitude: number; longitude: number } }
  | { success: false; status: string; message: string };

/**
 * Geocodes an address string to latitude and longitude coordinates.
 * @param addressString The address to geocode.
 * @param apiKey The Google Maps API key.
 * @returns A promise that resolves with a GeocodeResult object.
 */
export const getCoordinatesFromAddress = async (
  addressString: string,
  apiKey: string
): Promise<GeocodeResult> => {
  if (!addressString.trim()) {
    console.warn('getCoordinatesFromAddress: addressString is empty.');
    return { success: false, status: 'EMPTY_ADDRESS', message: 'Address string is empty.' };
  }
  if (!apiKey) {
    console.error('getCoordinatesFromAddress: apiKey is missing.');
    return { success: false, status: 'MISSING_API_KEY', message: 'API key is missing for geocoding.' };
  }

  try {
    await ensureGoogleMapsScriptLoaded(apiKey);
    const googleGlobal = (window as GoogleWindow).google;

    if (!googleGlobal || !googleGlobal.maps || !(googleGlobal.maps as any).Geocoder) {
      console.error('Google Maps Geocoder not available after script load.');
      return { success: false, status: 'GEOCODER_UNAVAILABLE', message: 'Google Maps Geocoder not available.' };
    }
    
    const geocoder = new (googleGlobal.maps as any).Geocoder();
    
    return new Promise((resolve) => { // Removed reject as we always resolve with GeocodeResult
      (geocoder as any).geocode({ address: addressString }, (results: any, status: any) => { // Use 'any' for results and status
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            success: true,
            coordinates: {
              latitude: location.lat(),
              longitude: location.lng(),
            },
          });
        } else {
          console.warn(`Geocoding failed for address "${addressString}": ${status}`);
          resolve({ success: false, status: status, message: `Geocoding failed: ${status}` });
        }
      });
    });
  } catch (error) {
    console.error('Error during geocoding process:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    return { success: false, status: 'CLIENT_ERROR', message: `Error during geocoding: ${errorMessage}` };
  }
};
